import { useCallback } from 'react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Rule } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { formatDateTime, ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { AvailabilityExceptionInfo, EventData, WorkingHourInfo } from '../../components/calendar-view/types';
import { DATETIME_FORMATS, REQUEST_EVENT_BACKGROUND_COLOR } from '../../constants';
import { getDateRangeFromRRule, getScheduleInfoFromRRule } from '../../rrule-helper';
import { useCalendarStore } from '../../stores/calendar.store';
import { CalendarEventsV3DateFormat } from '../../utils';
import { getCalendarStartAndEndDateTime, getDateString, getTimeString } from '../../utils/date-helpers';
import { CalendarEntitySchedule } from './calendar-schedule-definitions';

type GetScheduleRequestByProviderIdParamsType = {
  scheduleRequests: ScheduleRequestEvent[] | undefined;
  providerId: string;
  locationId: string;
  providerName: string;
};

type GetScheduleRequestByOperatoryIdParamsType = {
  scheduleRequests: ScheduleRequestEvent[] | undefined;
  operatoryId?: string | null;
  locationId: string;
};

type GetCalendarEventByProviderIdParamsType = {
  calendarEvents: CalendarEvent[];
  providerId: string;
  locationId: string;
};

type GetCalendarEventByOperatoryIdParamsType = {
  calendarEvents: CalendarEvent[];
  operatoryId?: string | null;
  locationId: string;
};

type GetEntityOutOfOfficeEventsArgs = {
  schedules: CalendarEntitySchedule[];
  entityId: string;
  locationId: string;
};

const getExceptionRelatedInfoFromRRule = (
  rrule: string,
  duration: number,
  calendarStartDateTime: string,
  calendarEndDateTime: string
) => {
  const { startDateTime, endDateTime, tzid } = getDateRangeFromRRule(rrule);
  const calendarStartUtc = dayjs(calendarStartDateTime).utc().format(DATETIME_FORMATS.ISO_DATETIME);
  const calendarEndUtc = dayjs(calendarEndDateTime).utc().format(DATETIME_FORMATS.ISO_DATETIME);

  const startUTCDateTime = formatDateTime(startDateTime, {
    fromTimezone: tzid ?? '',
    toTimezone: 'UTC',
    outputFormat: CalendarEventsV3DateFormat,
  });
  const endUTCDateTime = formatDateTime(dayjs.utc(endDateTime).add(duration, 'minute'), {
    fromTimezone: tzid ?? '',
    toTimezone: 'UTC',
    outputFormat: CalendarEventsV3DateFormat,
  });

  const shouldShowEvent =
    dayjs(calendarStartUtc).isBetween(startUTCDateTime, endUTCDateTime, null, '[)') ||
    dayjs(calendarEndUtc).isBetween(startUTCDateTime, endUTCDateTime, null, '[]') ||
    dayjs(startUTCDateTime).isBetween(calendarStartUtc, calendarEndUtc, null, '[]') ||
    dayjs(endUTCDateTime).isBetween(calendarStartUtc, calendarEndUtc, null, '(]');

  return { startDateTime, startUTCDateTime, endDateTime, endUTCDateTime, shouldShowEvent };
};

export const useGetCalendarEventsV3 = () => {
  const { t } = useTranslation('schedule');
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');

  const getMappedCalendarEventData = (calendarEvent: CalendarEvent): EventData => {
    const hasName = calendarEvent.attendeeDetails?.firstName || calendarEvent.attendeeDetails?.lastName;
    const personName = `${calendarEvent.attendeeDetails?.firstName} ${calendarEvent?.attendeeDetails?.lastName}`.trim();
    const name = hasName ? personName : 'Unknown';

    const startDate = dayjs(`${calendarEvent.startDate} ${calendarEvent.startTime}`);
    const endDate = dayjs(`${calendarEvent.endDate} ${calendarEvent.endTime}`);

    return {
      id: calendarEvent.id ?? '',
      isNonIntegratedSource: !calendarEvent.isIntegrated,
      startHour: getTimeString(startDate),
      endHour: getTimeString(endDate),
      type: calendarEvent.details?.rTyNa ?? '',
      status: calendarEvent.attendeeStatus,
      appointmentType: calendarEvent.eventType,
      appointmentTypeId: calendarEvent.referenceTypeId ?? '',
      eventId: calendarEvent.id ?? '',
      createdBySourceId: calendarEvent.sourceTenantId ?? '',
      workstationIds: calendarEvent.operatoryId ? [calendarEvent.operatoryId] : [],
      name: name,
      patientId: calendarEvent.attendeeId ?? '',
      locationId: calendarEvent.locationId ?? '',
      startDate: getDateString(startDate),
      endDate: getDateString(endDate),
      referenceId: calendarEvent.referenceId ?? '',
      personStatus: calendarEvent.attendeeDetails?.status ?? '',
      displayOptions: {
        primaryColor: calendarEvent.displayOptions?.primaryColor,
        backgroundColor: calendarEvent.displayOptions?.backgroundColor ?? undefined,
        textColor: calendarEvent.displayOptions?.textColor ?? undefined,
      },
    };
  };

  const getMappedScheduleRequestData = (scheduleRequest: ScheduleRequestEvent): EventData => {
    const startDate = dayjs(`${scheduleRequest.startDate} ${scheduleRequest.startTime}`);
    const endDate = dayjs(`${scheduleRequest.endDate} ${scheduleRequest.endTime}`);

    return {
      name: scheduleRequest.request.appointmentType || '',
      eventId: scheduleRequest.request.id || '',
      type: scheduleRequest.type || '',
      eventType: 'request',
      appointmentType: scheduleRequest.request.appointmentType,
      appointmentTypeId: scheduleRequest.referenceTypeId,
      locationId: scheduleRequest.request.locationId || '',
      startHour: getTimeString(startDate),
      endHour: getTimeString(endDate),
      startDate: getDateString(startDate),
      endDate: getDateString(endDate),
      backgroundColor: REQUEST_EVENT_BACKGROUND_COLOR,
      displayOptions: {
        primaryColor: scheduleRequest.displayOptions.primaryColor,
        backgroundColor: scheduleRequest.displayOptions.backgroundColor,
      },
      request: scheduleRequest.request,
    };
  };

  const getProviderCalendarEvents = useCallback(
    ({ calendarEvents, locationId, providerId }: GetCalendarEventByProviderIdParamsType): EventData[] => {
      return (calendarEvents ?? [])
        .filter(
          (event) =>
            event.organizerId !== undefined && event.locationId === locationId && event.organizerId === providerId //  event.organizerId is '' for unassigned providers
        )
        .map((calendarEvent) => getMappedCalendarEventData(calendarEvent));
    },
    []
  );

  const getOperatoryCalendarEvents = useCallback(
    ({ calendarEvents, locationId, operatoryId }: GetCalendarEventByOperatoryIdParamsType): EventData[] => {
      return (calendarEvents ?? [])
        .filter(
          (event) =>
            event.operatoryId !== undefined && event.locationId === locationId && event.operatoryId === operatoryId //  event.operatoryId is '' for unassigned operatories
        )
        .map((calendarEvent) => getMappedCalendarEventData(calendarEvent));
    },
    []
  );

  const getProviderScheduleRequestEvents = useCallback(
    ({
      scheduleRequests,
      locationId,
      providerId,
      providerName,
    }: GetScheduleRequestByProviderIdParamsType): EventData[] => {
      return (scheduleRequests ?? [])
        .filter((sr) => {
          return sr.locationId === locationId && (providerId === sr.organizerId || providerName === sr.providerName);
        })
        .map((request) => getMappedScheduleRequestData(request));
    },
    []
  );

  const getOperatoryScheduleRequestEvents = useCallback(
    ({ scheduleRequests, locationId, operatoryId }: GetScheduleRequestByOperatoryIdParamsType): EventData[] => {
      return (scheduleRequests ?? [])
        .filter((sr) => {
          return sr.locationId === locationId && operatoryId === sr.operatoryId;
        })
        .map((request) => getMappedScheduleRequestData(request));
    },
    []
  );

  const getEntityAvailabilityExceptions = useCallback(
    ({
      schedules,
      locationId,
      entityId,
    }: GetEntityOutOfOfficeEventsArgs): {
      availabilityExceptions: AvailabilityExceptionInfo[];
      isAllDayOff: boolean;
    } => {
      const locationTimezone = locationTimezoneMap[locationId]?.timezone;
      const { startDateTime: calendarStartDateTime, endDateTime: calendarEndDateTime } = getCalendarStartAndEndDateTime(
        calendarDate,
        locationTimezone
      );

      if (!calendarStartDateTime || !calendarEndDateTime) {
        return { availabilityExceptions: [], isAllDayOff: false };
      }

      const entitySchedule = schedules.find(
        (schedule) => schedule.locationId === locationId && schedule.id === entityId
      );

      if (entitySchedule && 'resolvedExceptions' in entitySchedule) {
        return {
          availabilityExceptions: entitySchedule.resolvedExceptions ?? [],
          isAllDayOff: false,
        };
      }

      const availabilityExceptions = entitySchedule?.recurrenceRules?.override?.availabilities || [];
      const unavailabilityExceptions = entitySchedule?.recurrenceRules?.override?.unAvailabilities || [];

      const isAllDayOff = unavailabilityExceptions.some(({ rrule = '', duration = 0 }) => {
        const info = getExceptionRelatedInfoFromRRule(rrule, duration, calendarStartDateTime, calendarEndDateTime);
        if (!rrule || !duration || !info.shouldShowEvent) return false;

        const isFullDayEvent = duration === 1440 - 1; // (24 * 60 minutes - 1 minute) to account for rrule end time exclusivity
        return isFullDayEvent;
      });

      const availabilityExceptionList = availabilityExceptions
        .map<AvailabilityExceptionInfo | null>(({ rrule = '', duration = 0 }) => {
          const info = getExceptionRelatedInfoFromRRule(rrule, duration, calendarStartDateTime, calendarEndDateTime);
          if (!rrule || !duration || !info.shouldShowEvent) return null;

          return {
            startTime: formatDateTime(info.startUTCDateTime, {
              fromTimezone: 'UTC',
              toTimezone: locationTimezone,
              outputFormat: 'hh:mm A',
            }),
            duration,
          };
        })
        .filter((item) => item !== null);

      return { availabilityExceptions: availabilityExceptionList, isAllDayOff };
    },
    [calendarDate, locationTimezoneMap]
  );

  const getEntityWorkingHours = useCallback(
    ({ schedules, locationId, entityId }: GetEntityOutOfOfficeEventsArgs): WorkingHourInfo[] => {
      const entitySchedule = schedules.find(
        (schedule) => schedule.locationId === locationId && schedule.id === entityId
      );

      if (entitySchedule && 'resolvedWorkingHours' in entitySchedule) {
        return entitySchedule.resolvedWorkingHours ?? [];
      }

      const scheduleWorkingHours = entitySchedule?.recurrenceRules?.workingHours || [];
      const scheduleBreakHours = entitySchedule?.recurrenceRules?.breaks || [];

      const workingHours = scheduleWorkingHours.reduce((acc: WorkingHourInfo[], { rrule, duration = 0 }: Rule) => {
        if (!rrule) return acc;

        const { startTime, daysOfWeek } = getScheduleInfoFromRRule(rrule, duration, 'hh:mm A');

        if (daysOfWeek.includes(dayjs(calendarDate).day())) {
          acc.push({ startTime, duration, breaks: [] });
        }
        return acc;
      }, []);

      const breakHours = scheduleBreakHours.reduce(
        (acc: Pick<WorkingHourInfo, 'startTime' | 'duration'>[], { rrule, duration = 0 }: Rule) => {
          if (!rrule) return acc;

          const { startTime, daysOfWeek } = getScheduleInfoFromRRule(rrule, duration, 'hh:mm A');

          if (daysOfWeek.includes(dayjs(calendarDate).day())) {
            acc.push({ startTime, duration });
          }
          return acc;
        },
        []
      );

      return workingHours.map((workingHour) => ({ ...workingHour, breaks: breakHours }));
    },
    [calendarDate]
  );

  const getEntityExceptionEvents = useCallback(
    ({ schedules, locationId, entityId }: GetEntityOutOfOfficeEventsArgs): EventData[] => {
      const { startDateTime: calendarStartDateTime, endDateTime: calendarEndDateTime } = getCalendarStartAndEndDateTime(
        calendarDate,
        locationTimezoneMap[locationId]?.timezone
      );

      if (!calendarStartDateTime || !calendarEndDateTime) {
        return [];
      }

      const entitySchedule = schedules.filter(
        (schedule) => schedule.locationId === locationId && schedule.id === entityId
      );

      const exceptions = entitySchedule[0]?.recurrenceRules?.override?.unAvailabilities || [];

      const result = exceptions.map<EventData>(({ id = '', name = '', rrule, duration = 0 }: Rule): any => {
        if (rrule) {
          const info = getExceptionRelatedInfoFromRRule(rrule, duration, calendarStartDateTime, calendarEndDateTime);

          if (info.shouldShowEvent) {
            // NOTE: The event start and end dates could be different than the calendar date in case of multi-day recurring events.
            // But we need to show the event on the calendar for selected calendar date. So, using the calendar date for start and end dates.
            const startDate = dayjs(calendarDate).format('MM/DD/YYYY');
            const startHour = dayjs.utc(info.startDateTime).format('hh:mm A');
            const endDateTime = dayjs(`${calendarDate} ${startHour}`).add(duration, 'minute');
            const endDate = endDateTime.format('MM/DD/YYYY');
            const endHour = endDateTime.format('hh:mm A');

            return {
              id,
              name: name?.trim() || t('Unavailable'),
              type: 'unavailable',
              startDate,
              startHour,
              endDate,
              endHour,
              eventId: id,
              locationId: locationId,
            };
          }

          return null;
        }

        return null;
      });

      return result.filter((item) => item !== null);
    },
    [calendarDate, locationTimezoneMap, t]
  );

  return {
    getProviderCalendarEvents,
    getOperatoryCalendarEvents,
    getEntityWorkingHours,
    getEntityExceptionEvents,
    getEntityAvailabilityExceptions,
    getProviderScheduleRequestEvents,
    getOperatoryScheduleRequestEvents,
  };
};
